/**
 * Provider Trainings section types
 *
 * Supports: current/past trainings, certifications, required hours remaining.
 * Aligns with Notion Requirements (Provider Documentation, Provider: Trainings).
 * Assumption: CE/training period is annual (rolling 12 months) unless specified per program.
 */

export type TrainingStatus =
  | 'not-started'
  | 'in-progress'
  | 'completed'
  | 'overdue'
  | 'expired';

export type VerificationStatus = 'pending' | 'verified' | 'rejected';

export interface Training {
  id: string;
  name: string;
  required: boolean;
  status: TrainingStatus;
  completionDate: string | null;
  expirationDate: string | null;
  completedBy?: string;
  verifiedAt?: string | null;
  verificationStatus?: VerificationStatus;
  /** Downstream impact when overdue/expired (e.g. supervision, credentialing) */
  enforcementNote?: string;
}

export interface Certification {
  id: string;
  name: string;
  issuingBody: string;
  issueDate: string;
  expirationDate: string | null;
  renewalDate: string | null;
  verificationStatus: VerificationStatus;
  verifiedAt: string | null;
  /** Days until expiration; negative = expired */
  daysUntilExpiration?: number;
  attachmentUrl?: string | null;
}

export interface HoursSummary {
  periodLabel: string;
  periodStart: string;
  periodEnd: string;
  requiredHours: number;
  completedHours: number;
  remainingHours: number;
  /** e.g. "CE" or "Training" */
  category: string;
}

export interface TrainingsFilters {
  /** Selected statuses; empty = none. */
  status: TrainingStatus[];
  requiredOnly: boolean;
  search: string;
}

export const defaultTrainingsFilters: TrainingsFilters = {
  status: ['not-started', 'in-progress', 'completed', 'overdue', 'expired'],
  requiredOnly: false,
  search: '',
};

export const TrainingStatusLabels: Record<TrainingStatus, string> = {
  'not-started': 'Not started',
  'in-progress': 'In progress',
  completed: 'Completed',
  overdue: 'Overdue',
  expired: 'Expired',
};

export const TrainingStatusColors: Record<TrainingStatus, string> = {
  'not-started': 'gray',
  'in-progress': 'blue',
  completed: 'green',
  overdue: 'orange',
  expired: 'red',
};

export const VerificationStatusLabels: Record<VerificationStatus, string> = {
  pending: 'Pending',
  verified: 'Verified',
  rejected: 'Rejected',
};
